import { useState } from "react";
import { useLocalSearchParams } from "expo-router";
import {
  isTicketActive,
  TICKET_LIMITS,
  TICKET_STATUS_LABELS,
  TICKET_TYPE_LABELS,
} from "@brad-tipper/app-frontend/tickets";

import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  ProtectedScreen,
  Row,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import {
  useAddTicketNote,
  useSetTicketStatus,
  useTicket,
} from "~/features/support/tickets";

export default function TicketScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const ticket = useTicket(id);
  const addNote = useAddTicketNote(id ?? "");
  const setStatus = useSetTicketStatus(id ?? "");
  const [note, setNote] = useState("");

  const data = ticket.data;
  const active = data ? isTicketActive(data.status) : true;

  async function send() {
    const body = note.trim();
    if (!body) return;
    await addNote.mutateAsync(body);
    setNote("");
  }

  if (!data) {
    return (
      <ProtectedScreen title="Support">
        <AppText tone={ticket.error ? "danger" : "muted"}>
          {ticket.error ? "Unable to load this ticket." : "Loading…"}
        </AppText>
      </ProtectedScreen>
    );
  }

  return (
    <ProtectedScreen
      narrow
      title={`#${data.number} ${data.title}`}
      subtitle={`${TICKET_TYPE_LABELS[data.type]} · opened ${new Date(data.createdAt).toLocaleDateString()}`}
    >
      <Card gap="sm">
        <Row>
          <Badge tone={active ? "mint" : "neutral"}>
            {TICKET_STATUS_LABELS[data.status]}
          </Badge>
          {data.awaitingSupport ? (
            <AppText size="xs" tone="muted">
              Waiting for our reply
            </AppText>
          ) : null}
        </Row>
        <AppText>{data.description}</AppText>
      </Card>

      <Stack gap="sm">
        <SectionHeader title="Conversation" />
        {data.messages.length === 0 ? (
          <AppText tone="muted" size="sm">
            No notes yet. We will email you when we reply.
          </AppText>
        ) : (
          data.messages.map((message) => (
            <Card key={message.id} compact gap="xs">
              <AppText size="xs" tone="muted">
                {message.from === "support" ? "Support" : "You"} ·{" "}
                {new Date(message.createdAt).toLocaleString()}
              </AppText>
              <AppText>{message.body}</AppText>
            </Card>
          ))
        )}
      </Stack>

      <Card gap="sm">
        <SectionHeader
          title={active ? "Add a note" : "Add a note (reopens this ticket)"}
        />
        <TextField
          accessibilityLabel="Note"
          value={note}
          onChangeText={setNote}
          maxLength={TICKET_LIMITS.note}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
          placeholder="Anything else we should know?"
        />
        {addNote.error ? (
          <AppText tone="danger" size="sm">
            {addNote.error instanceof Error
              ? addNote.error.message
              : "Unable to send."}
          </AppText>
        ) : null}
        {setStatus.error ? (
          <AppText tone="danger" size="sm">
            {setStatus.error instanceof Error
              ? setStatus.error.message
              : "Unable to update the ticket."}
          </AppText>
        ) : null}
        <Row>
          <Button
            align="start"
            disabled={!note.trim() || addNote.isPending}
            onPress={() => void send()}
          >
            <ButtonText>
              {addNote.isPending ? "Sending…" : "Send note"}
            </ButtonText>
          </Button>
          <Button
            variant="outline"
            align="start"
            disabled={setStatus.isPending}
            onPress={() =>
              void setStatus.mutateAsync(active ? "closed" : "open")
            }
          >
            <ButtonText>{active ? "Close ticket" : "Reopen"}</ButtonText>
          </Button>
        </Row>
      </Card>
    </ProtectedScreen>
  );
}
